import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  MatSort,
  MatSortModule,
  Sort,
  SortDirection,
} from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
  AuditLog,
  AuditLogListRequest,
  EnumValue,
  LogService,
} from '../../../services/log.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { AuditDetailDialogComponent } from './audit-detail-dialog/audit-detail-dialog.component';

@Component({
  selector: 'app-audit-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ErrorStateComponent,
  ],
  templateUrl: './audit-history.component.html',
  styleUrls: ['./audit-history.component.scss'],
})
export class AuditHistoryComponent implements OnInit, OnDestroy {
  private logService = inject(LogService);
  private snackbarService = inject(SnackbarService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);

  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);

  private destroy$ = new Subject<void>();

  // Data
  dataSource = new MatTableDataSource<AuditLog>([]);
  displayedColumns: string[] = [
    'timestamp',
    'action',
    'entityType',
    'entityId',
    'user',
    'message',
  ];

  // Filter options
  auditLogTypes: EnumValue[] = [];
  entityTypes: EnumValue[] = [];

  // State
  isLoading = false;
  error: string | null = null;

  // Pagination
  totalCount = 0;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [10, 25, 50, 100];

  // Sorting
  sortBy = 'timestamp';
  sortDirection: SortDirection = 'desc';

  // Search/Filter
  filterForm!: FormGroup;
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.initializeFilterForm();
    this.loadFilterOptions();
    this.loadAuditLogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFilterForm(): void {
    // Generate default date values for today
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startOfDay = `${year}-${month}-${day}T00:00`;
    const endOfDay = `${year}-${month}-${day}T23:59`;

    this.filterForm = this.fb.group({
      actionTypes: [[]],
      entityTypes: [[]],
      startDate: [startOfDay],
      endDate: [endOfDay],
    });

    // Subscribe to dropdown changes
    this.filterForm
      .get('actionTypes')
      ?.valueChanges.pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadAuditLogs();
      });

    this.filterForm
      .get('entityTypes')
      ?.valueChanges.pipe(debounceTime(0), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadAuditLogs();
      });
  }

  onDateChange(): void {
    this.pageIndex = 0;
    this.loadAuditLogs();
  }

  loadFilterOptions(): void {
    this.logService.getAuditLogTypes().subscribe({
      next: (types) => {
        // Filter out 'Read' since we haven't implemented read logs yet
        this.auditLogTypes = types.filter(
          (t) => t.name.toLowerCase() !== 'read',
        );
      },
      error: (error) => {
        console.error('Error loading audit log types:', error);
      },
    });

    this.logService.getDatabaseEntityTypes().subscribe({
      next: (types) => {
        this.entityTypes = types;
      },
      error: (error) => {
        console.error('Error loading entity types:', error);
      },
    });
  }

  loadAuditLogs(): void {
    this.isLoading = true;
    this.error = null;

    const formValue = this.filterForm.value;
    const actionTypes = formValue.actionTypes || [];
    const entityTypes = formValue.entityTypes || [];

    // Format dates as ISO strings if provided
    const startDate = formValue.startDate
      ? this.formatDateForApi(formValue.startDate, true)
      : undefined;
    const endDate = formValue.endDate
      ? this.formatDateForApi(formValue.endDate, false)
      : undefined;

    const request: AuditLogListRequest = {
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      search: this.searchQuery?.trim() || undefined,
      auditLogTypes: actionTypes.length > 0 ? actionTypes : undefined,
      databaseEntityTypes: entityTypes.length > 0 ? entityTypes : undefined,
      startDate,
      endDate,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
    };

    this.logService.getAuditLogs(request).subscribe({
      next: (result) => {
        this.dataSource.data = result.items;
        this.totalCount = result.totalCount;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading audit logs:', error);
        this.error = 'Failed to load audit logs. Please try again.';
        this.snackbarService.showError('Failed to load audit logs');
        this.isLoading = false;
      },
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadAuditLogs();
  }

  onSortChange(sort: Sort): void {
    this.sortBy = sort.active || 'timestamp';
    this.sortDirection = sort.direction || 'desc';
    this.pageIndex = 0;
    this.loadAuditLogs();
  }

  onSearchInput(): void {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.pageIndex = 0;
      this.loadAuditLogs();
    }, 400);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.pageIndex = 0;
    this.loadAuditLogs();
  }

  resetFilters(): void {
    this.searchQuery = '';

    // Reset to default values (today's date range)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startOfDay = `${year}-${month}-${day}T00:00`;
    const endOfDay = `${year}-${month}-${day}T23:59`;

    this.filterForm.reset({
      actionTypes: [],
      entityTypes: [],
      startDate: startOfDay,
      endDate: endOfDay,
    });

    // Reset sorting to default (timestamp descending)
    this.sortBy = 'timestamp';
    this.sortDirection = 'desc';
    const sort = this.sort();
    sort.active = 'timestamp';
    sort.direction = 'desc';
    sort.sortChange.emit({ active: 'timestamp', direction: 'desc' });

    this.pageIndex = 0;
    this.loadAuditLogs();
  }

  hasActiveFilters(): boolean {
    const formValue = this.filterForm.value;
    return !!(
      this.searchQuery?.trim() ||
      (formValue.actionTypes && formValue.actionTypes.length > 0) ||
      (formValue.entityTypes && formValue.entityTypes.length > 0) ||
      formValue.startDate ||
      formValue.endDate
    );
  }

  hasFiltersChanged(): boolean {
    // Check if any filters differ from their default values
    const formValue = this.filterForm.value;

    // Get today's default date range
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const defaultStartDate = `${year}-${month}-${day}T00:00`;
    const defaultEndDate = `${year}-${month}-${day}T23:59`;

    // Check if sorting differs from default (timestamp desc)
    const sortingChanged =
      this.sortBy !== 'timestamp' || this.sortDirection !== 'desc';

    return !!(
      this.searchQuery?.trim() ||
      (formValue.actionTypes && formValue.actionTypes.length > 0) ||
      (formValue.entityTypes && formValue.entityTypes.length > 0) ||
      formValue.startDate !== defaultStartDate ||
      formValue.endDate !== defaultEndDate ||
      sortingChanged
    );
  }

  private formatDateForApi(date: string | Date, isStartDate: boolean): string {
    // datetime-local returns a string in format "YYYY-MM-DDTHH:mm" in local time
    // We need to convert to UTC since timestamps are stored in UTC in the database
    let localDate: Date;

    if (typeof date === 'string') {
      // Parse the datetime-local string as local time
      localDate = new Date(date);
    } else {
      localDate = new Date(date);
    }

    // Convert to UTC ISO string and return without the 'Z' suffix
    // toISOString() returns UTC time in format "YYYY-MM-DDTHH:mm:ss.sssZ"
    return localDate.toISOString().slice(0, 19);
  }

  getActionClass(typeName: string): string {
    switch (typeName?.toLowerCase()) {
      case 'create':
        return 'action-create';
      case 'read':
        return 'action-read';
      case 'update':
        return 'action-update';
      case 'delete':
        return 'action-delete';
      default:
        return '';
    }
  }

  formatTimestamp(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString();
  }

  truncateMessage(message: string, maxLength: number = 100): string {
    if (!message) return '';
    return message.length > maxLength
      ? message.substring(0, maxLength) + '...'
      : message;
  }

  openAuditDetail(log: AuditLog): void {
    this.dialog.open(AuditDetailDialogComponent, {
      data: { log },
      width: '700px',
      maxHeight: '90vh',
    });
  }
}
